import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import UrgencyBar from '@/components/feature/UrgencyBar';
import MobileStickyBar from '@/components/feature/MobileStickyBar';
import AlbumCard from './components/AlbumCard';
import AlbumDetail from './components/AlbumDetail';
import { ALBUMS, Album } from '@/mocks/portfolio';

const FILTERS = ['All', 'Flooring', 'Bathroom', 'Tile Work', 'Demolition'];

export default function PortfolioPage() {
  const [activeFilter, setActiveFilter] = useState('All');
  const [openAlbum, setOpenAlbum] = useState<Album | null>(null);

  const filtered = activeFilter === 'All'
    ? ALBUMS
    : ALBUMS.filter((a) => a.category === activeFilter);

  return (
    <div className="min-h-screen bg-[#0d0d0d]">
      <UrgencyBar />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-36 pb-20 px-4 md:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#1c1a17] to-[#0d0d0d]" />
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #c9a55a 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#c9a55a]/10 border border-[#c9a55a]/25 rounded-full px-5 py-2 mb-8">
            <i className="ri-folder-image-line text-[#c9a55a] text-sm" />
            <span className="text-[#c9a55a] text-sm font-semibold tracking-wide">Real Projects. Real Results.</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-black text-white leading-tight mb-6">
            Our <span className="text-[#c9a55a]">Work</span>
          </h1>
          <p className="text-white/45 text-lg max-w-xl mx-auto leading-relaxed">
            Browse our project albums — each one is a real job completed by our team
            across Tampa Bay &amp; Sarasota. Click any album to see all photos.
          </p>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="border-y border-white/8 py-8 px-4 md:px-8">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { value: '2000+', label: 'Projects Completed' },
            { value: '10', label: 'Project Albums' },
            { value: '8+', label: 'Years Experience' },
            { value: '5★', label: 'Client Satisfaction' },
          ].map((stat) => (
            <div key={stat.label}>
              <div className="text-2xl md:text-3xl font-black text-[#c9a55a] mb-1">{stat.value}</div>
              <div className="text-white/40 text-xs uppercase tracking-wider">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Filter Bar */}
      <section className="py-10 px-4 md:px-8">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-center gap-3">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setActiveFilter(f)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                activeFilter === f
                  ? 'bg-[#c9a55a] text-[#0d0d0d]'
                  : 'bg-white/6 border border-white/12 text-white/55 hover:text-white hover:border-white/25'
              }`}
            >
              {f}
              {f !== 'All' && (
                <span className="ml-2 text-xs opacity-60">
                  ({ALBUMS.filter((a) => a.category === f).length})
                </span>
              )}
            </button>
          ))}
        </div>
      </section>

      {/* Albums Grid */}
      <section className="pb-24 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Section label */}
          <div className="flex items-center justify-between mb-8">
            <p className="text-white/30 text-sm">
              Showing <span className="text-white/60 font-semibold">{filtered.length}</span> albums
              {activeFilter !== 'All' && (
                <span> in <span className="text-[#c9a55a] font-semibold">{activeFilter}</span></span>
              )}
            </p>
            <p className="text-white/20 text-xs">Click an album to view all photos</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filtered.map((album) => (
              <AlbumCard
                key={album.id}
                album={album}
                onClick={setOpenAlbum}
              />
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20">
              <i className="ri-folder-open-line text-white/15 text-6xl mb-4 block" />
              <p className="text-white/30 text-base">No albums in this category yet.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1c1a17] border-t border-white/8 py-24 px-4 md:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-[#c9a55a] text-xs font-bold uppercase tracking-[0.3em] mb-5">Ready to Start?</p>
          <h2 className="text-3xl md:text-5xl font-black text-white mb-5 leading-tight">
            Your project could be<br />
            <span className="text-[#c9a55a]">our next album.</span>
          </h2>
          <p className="text-white/45 text-base mb-10 leading-relaxed max-w-xl mx-auto">
            Get a free estimate and let&apos;s talk about what we can build together.
            Serving Tampa Bay &amp; Sarasota.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 bg-[#c9a55a] hover:bg-[#b8944a] text-[#0d0d0d] font-bold text-lg px-12 py-4 rounded-lg transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-calendar-check-line" />
              Get a Free Estimate
            </Link>
            <a
              href="tel:+19044019383"
              className="inline-flex items-center gap-2 text-white/60 hover:text-white font-semibold text-base transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line text-lg" />
              (904) 401-9383
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <MobileStickyBar />

      {/* Album Detail Overlay */}
      {openAlbum && (
        <AlbumDetail
          album={openAlbum}
          onClose={() => setOpenAlbum(null)}
        />
      )}
    </div>
  );
}
